/**
 * OwnerAccessLogin — Wave 12.x · Advisor / Owner door.
 *
 *   Path:       /owner-access
 *   Authorized: any user whose role is `advisor`, `owner`, or `developer`.
 *   Refuses:    `client` credentials. A client who lands here will be
 *               logged out immediately and shown "These credentials are
 *               not for this door." This is the security separation
 *               requirement — the advisor door must never authenticate
 *               a member through the wrong UI.
 *
 * Design intent: visually identical aesthetic to JacksonSuiteLogin so
 * the two doors feel like a matched pair — same dark canvas, same gold
 * accent, same serif headline. Different copy, different audience.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { CLIENT_LOGIN_PATH } from "../lib/authPaths";

const API = process.env.REACT_APP_BACKEND_URL;
const ADVISOR_ROLES = new Set(["advisor", "owner", "developer"]);

export default function OwnerAccessLogin() {
  const { user, checkSession } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Already signed in as an advisor / owner → drop them straight into the portal.
  useEffect(() => {
    if (user && ADVISOR_ROLES.has((user.role || "").toLowerCase())) {
      navigate("/portal/advisor", { replace: true });
    }
    // Signed in as a client?  This door isn't for them — route them home.
    if (user && (user.role || "").toLowerCase() === "client") {
      navigate(CLIENT_LOGIN_PATH, { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");

    const normalized = email.trim().toLowerCase();
    try {
      const res = await axios.post(
        `${API}/api/auth/login`,
        { email: normalized, password },
        { withCredentials: true },
      );
      const role = (res?.data?.role || "").toLowerCase();
      if (!ADVISOR_ROLES.has(role)) {
        // Wrong door for these credentials. Drop the session immediately
        // so a client can't sneak into advisor chrome via this form.
        try {
          await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
        } catch (_e) { /* noop */ }
        setError("These credentials are not for this door. Please use your private member link.");
        setBusy(false);
        return;
      }
      await checkSession();
      navigate("/portal/advisor", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail.toLowerCase().includes("suite access has ended")) {
        setError("This account is no longer active. Please contact support.");
      } else {
        setError("Sign in didn't work. Please check your email and password.");
      }
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0c0a] text-[#f5f1ea] flex flex-col">
      <header className="px-6 sm:px-12 pt-10 pb-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#c9a96e]">
          VÉRAULT · Studio
        </p>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 sm:px-12 pb-16">
        <div className="w-full max-w-[420px]" data-testid="owner-access-login">
          <div className="mb-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8a7a5f] mb-3">
              Owner &amp; Advisor
            </p>
            <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] text-[#f5f1ea]">
              Welcome back.
            </h1>
            <p className="mt-5 font-accent italic text-[15px] leading-relaxed text-[#b7a98a] max-w-[360px]">
              Sign in to your studio workspace. Members have a separate,
              private access link delivered with their invitation.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" autoComplete="on">
            <div>
              <label
                htmlFor="owner-email"
                className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[#8a7a5f] mb-2"
              >
                Email
              </label>
              <input
                id="owner-email"
                type="email"
                name="username"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@verault.co"
                disabled={busy}
                className="w-full px-4 py-3 bg-[#191713] border border-[#3a342a] rounded-md text-[#f5f1ea] placeholder:text-[#5a5346] focus:outline-none focus:border-[#c9a96e] focus:ring-1 focus:ring-[#c9a96e]/40 transition-colors"
                data-testid="owner-access-email"
              />
            </div>

            <div>
              <label
                htmlFor="owner-password"
                className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[#8a7a5f] mb-2"
              >
                Password
              </label>
              <input
                id="owner-password"
                type="password"
                name="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                disabled={busy}
                className="w-full px-4 py-3 bg-[#191713] border border-[#3a342a] rounded-md text-[#f5f1ea] placeholder:text-[#5a5346] focus:outline-none focus:border-[#c9a96e] focus:ring-1 focus:ring-[#c9a96e]/40 transition-colors"
                data-testid="owner-access-password"
              />
            </div>

            {error && (
              <p
                className="font-accent italic text-[13px] text-[#d4836a] leading-relaxed"
                data-testid="owner-access-error"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={busy || !email || !password}
              className="w-full py-3 mt-2 bg-[#c9a96e] text-[#0d0c0a] font-mono text-[11px] uppercase tracking-[0.2em] rounded-md hover:bg-[#d9b87e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="owner-access-submit"
            >
              {busy ? "Entering…" : "Enter Studio"}
            </button>
          </form>

          <p className="mt-12 font-accent italic text-[12px] text-[#5a5346] leading-relaxed">
            Members: this is the studio door. Please use the private access
            link sent with your invitation.
          </p>
        </div>
      </main>

      <footer className="px-6 sm:px-12 pb-8">
        <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#3a342a]">
          Private · Encrypted · Studio-only
        </p>
      </footer>
    </div>
  );
}
